import React from 'react';
import { AlertTriangle, Check, Eye, EyeOff, Wand2 } from 'lucide-react';

import FieldError from '@/components/ui/field-error';
import { PhonePage, PrimaryButton, Spinner, TopBar } from '@/components/phone/primitives';
import { Field, INPUT_CLASS, inputStyle, SelectField } from '@/components/phone/FieldControls';
import { Colors } from '@/components/phone/theme';

/**
 * Adding or editing an account, on a phone.
 *
 * The same form the side pane holds, one control per row: the role opens as a
 * sheet, and the secondary roles, stages and product lines are chips the size
 * of a thumb rather than the 11px toggles the pane fits in beside the grid.
 */

/** Several of a list, as chips — the picker knows which are on, so it says. */
function Chips({ items, onToggle, name, empty }) {
  if (items.length === 0) {
    return <p className="text-[13px]" style={{ color: Colors.text.tertiary }}>{empty}</p>;
  }
  return (
    <div className="flex flex-wrap gap-2" data-testid={`field-${name}`}>
      {items.map(({ key, label, on, value }) => (
        <button
          key={key}
          type="button"
          aria-pressed={on}
          onClick={() => onToggle(value ?? key)}
          className="h-10 rounded-[10px] border px-3 text-[13px]"
          style={{
            backgroundColor: on ? Colors.accent.DEFAULT : Colors.background.primary,
            borderColor: on ? Colors.accent.DEFAULT : Colors.border.default,
            color: on ? Colors.text.inverse : Colors.text.secondary,
            fontWeight: on ? 600 : 500,
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function PhoneUserForm({
  mode, form, fieldErrors, onField, roleOptions,
  showPassword, setShowPassword, onGeneratePassword,
  onToggleSecondaryRole, allStages, stageRefs, onToggleStage, allTypes, onToggleType,
  submitting, onSave, onCancel,
}) {
  const adding = mode === 'add';
  const usernameChanged = !adding && form.username !== mode.username;
  const supervises = form.role === 'supervisor' || form.secondary_roles.includes('supervisor');

  return (
    <PhonePage testID="user-form-page">
      <TopBar
        eyebrow="ACCOUNT"
        title={adding ? 'Add user' : `Edit ${mode.full_name || 'user'}`}
        subtitle={adding ? 'They can sign in as soon as this is saved' : mode.username}
        onBack={onCancel}
        actions={(
          <button
            type="button"
            data-testid="submit-user-btn"
            onClick={onSave}
            disabled={submitting}
            className="flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-[13px] font-semibold disabled:opacity-60"
            style={{ backgroundColor: Colors.semantic.positive, color: Colors.text.inverse }}
          >
            {submitting ? <Spinner size="sm" className="!border-white !border-b-transparent" /> : <><Check className="h-4 w-4" /> Save</>}
          </button>
        )}
      />

      <div className="flex-1 space-y-3 px-4 pb-10 pt-4">
        <Field label="Full name *">
          <input
            data-testid="user-name-input"
            className={INPUT_CLASS} style={inputStyle} autoCapitalize="words"
            value={form.full_name}
            onChange={(e) => onField('full_name', e.target.value)}
          />
          <FieldError error={fieldErrors.full_name} />
        </Field>

        <Field label="Username *">
          <input
            data-testid="user-username-input"
            className={INPUT_CLASS} style={inputStyle} autoCapitalize="none" autoCorrect="off"
            value={form.username}
            onChange={(e) => onField('username', e.target.value)}
          />
          <FieldError error={fieldErrors.username} />
        </Field>

        {usernameChanged && (
          <p
            className="flex items-start gap-1.5 rounded-[10px] border px-3 py-2 text-[12px]"
            style={{ backgroundColor: Colors.semantic.cautionWash, borderColor: '#E8DCC4', color: Colors.semantic.caution }}
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Signing in as <strong>{mode.username}</strong> will stop working. They will need to use{' '}
              <strong>{form.username || '—'}</strong> from their next sign-in.
            </span>
          </p>
        )}

        <Field label="Role *">
          <SelectField name="role" label="Role" value={form.role} options={roleOptions} onChange={(v) => onField('role', v)} placeholder="Pick a role" />
          <FieldError error={fieldErrors.role} />
        </Field>

        {adding && (
          <Field label="Password *">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  data-testid="user-password-input"
                  className={`${INPUT_CLASS} pr-11`}
                  style={inputStyle}
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => onField('password', e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center"
                  style={{ color: Colors.text.tertiary }}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <button
                type="button"
                onClick={onGeneratePassword}
                aria-label="Generate a password that is easy to read out"
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] border"
                style={{ borderColor: Colors.border.default, color: Colors.accent.DEFAULT }}
              >
                <Wand2 className="h-4 w-4" />
              </button>
            </div>
            <FieldError error={fieldErrors.password} />
          </Field>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Mobile">
            <input
              className={INPUT_CLASS} style={inputStyle} inputMode="tel"
              value={form.mobile_number}
              onChange={(e) => onField('mobile_number', e.target.value)}
            />
          </Field>
          <Field label="Email">
            <input
              className={INPUT_CLASS} style={inputStyle} inputMode="email" autoCapitalize="none"
              value={form.email}
              onChange={(e) => onField('email', e.target.value)}
            />
            <FieldError error={fieldErrors.email} />
          </Field>
        </div>

        <Field label={`Also works as${form.secondary_roles.length ? ` (${form.secondary_roles.length})` : ''}`}>
          <Chips
            name="secondary_roles"
            items={roleOptions.filter((r) => r.value !== form.role).map((r) => ({
              key: r.value, label: r.label, on: form.secondary_roles.includes(r.value),
            }))}
            onToggle={onToggleSecondaryRole}
            empty="No other roles to add"
          />
        </Field>

        {supervises && (
          <>
            <Field label={`Covers which stages${form.linked_stage_ids.length ? ` (${form.linked_stage_ids.length} selected)` : ''}`}>
              <Chips
                name="linked_stages"
                items={allStages.map((stage) => {
                  const name = stage.stage_name || stage.name;
                  return {
                    key: stage.id || stage.stage_key || name,
                    label: name,
                    value: stage,
                    on: form.linked_stage_ids.some((s) => stageRefs(stage).includes(s)),
                  };
                })}
                onToggle={onToggleStage}
                empty="No stages available"
              />
            </Field>
            <Field
              label={`Handles which types${form.linked_product_types.length ? ` (${form.linked_product_types.length} selected)` : ''}`}
              hint="Packing, loading and his board are limited to these. None selected means every type."
            >
              <Chips
                name="linked_product_types"
                items={allTypes.map((type) => ({
                  key: type.code,
                  label: type.short_name || type.name || type.code,
                  on: form.linked_product_types.includes(type.code),
                }))}
                onToggle={onToggleType}
                empty="No product types available"
              />
            </Field>
          </>
        )}

        <PrimaryButton className="mt-5" onClick={onSave} busy={submitting} testID="submit-user-bottom">
          <Check className="h-4 w-4" /> {adding ? 'Create user' : 'Save changes'}
        </PrimaryButton>
      </div>
    </PhonePage>
  );
}
